import type { MetadataFieldWithValue } from '@/model/Metadata.model';
import { ErrorMessage } from '@hookform/error-message';
import type { FC, ReactNode } from 'react';
import { useFormContext } from 'react-hook-form-latest';
import { DatePicker } from './Fieldtypes/Latest/DatePicker';
import { NumberField } from './Fieldtypes/Latest/Number';
import { Select } from './Fieldtypes/Latest/Select';
import { SelectLookup } from './Fieldtypes/Latest/SelectLookup';
import { Text } from './Fieldtypes/Latest/Text';

interface ErrorHandlingProps {
  children: ReactNode;
  fieldKey: string | undefined;
}

const ErrorHandling: FC<ErrorHandlingProps> = ({ children, fieldKey }) => {
  const {
    formState: { errors },
  } = useFormContext();

  return (
    <div>
      {children}
      <ErrorMessage
        errors={errors}
        name={`metadata[${fieldKey}].value`}
        render={({ message }) => <p className="f6 red db pt2">{message}</p>}
      />
    </div>
  );
};

const FieldMap = (field: MetadataFieldWithValue) => {
  const testId = `metadata-field__${field.slug}`;
  const fieldName = `metadata[${field.glossary_api_id}].value`;
  return {
    text: <Text field={field} testId={testId} />,
    date: <DatePicker field={field} testId={testId} />,
    select: <Select field={field} testId={testId} />,
    multi_select: <Select field={field} isMulti testId={testId} />,
    lookup_select: <SelectLookup field={field} testId={testId} name={fieldName} />,
    lookup_multi_select: <SelectLookup field={field} isMulti testId={testId} name={fieldName} />,
    numeric: <NumberField field={field} testId={testId} />,
  };
};

interface RenderedFieldProps {
  metadata: MetadataFieldWithValue;
  required: boolean;
}

export const RenderedField = ({ metadata, required }: RenderedFieldProps) => {
  const { register } = useFormContext();

  return (
    <div className="mb3 w-90">
      <label
        htmlFor={`metadata[${metadata.glossary_api_id}].value`}
        data-testid={`metadata-field__${metadata.slug}--label`}
      >
        {metadata.title}
        {required === true && ' (required)'}
      </label>
      <input
        type="hidden"
        {...register(`metadata[${metadata.glossary_api_id}].glossary_id`, {
          required: 'Existing fields cannot be empty',
        })}
        defaultValue={metadata.glossary_id}
      />
      <ErrorHandling fieldKey={metadata.glossary_api_id}>
        {
          // Setting required to true for all fields as they cannot be empty on settings edit
          FieldMap({ ...metadata, required: true, id: metadata.glossary_id, value: metadata.value })[
            metadata.field_type
          ]
        }
      </ErrorHandling>
    </div>
  );
};
